import { DatasetType } from '@/types/automationFields.ts';
import { MS_OFFICE_EXCEL_MIME_TYPES } from '@/utils/mimeTypes.ts';

// since we sometimes treat spreadsheets as directories, we need to exclude them from the directory check
import { Dataset } from '@/types/datasets.ts';

const SUPPORTED_IMAGE_TYPES = ['image/png', 'image/jpeg'];

export const isDirectory = (dataset?: Dataset) => {
  return !!dataset && dataset.is_natual_directory;
};

export const isGif = (dataset: Dataset) => {
  return dataset.has_original_blob && dataset.original_mime_type === 'image/gif';
};

export const isVideo = (dataset: Dataset) => {
  return dataset.has_original_blob && dataset.original_mime_type === 'video/mp4';
};

export const isImage = (dataset: Dataset) => {
  return (
    dataset.has_original_blob &&
    dataset.original_mime_type !== null &&
    SUPPORTED_IMAGE_TYPES.includes(dataset.original_mime_type)
  );
};

export const isPdf = (dataset: Dataset) => {
  return dataset.has_original_blob && dataset.original_mime_type === 'application/pdf';
};

// export const isExtractStepOutput = (dataset: Dataset) => {
//   return dataset.is_output_dataset && dataset.is_extract_output;
// };

export const isJson = (dataset: Dataset) => {
  return (
    (dataset.has_original_blob && dataset.original_mime_type === 'application/json') ||
    ([DatasetType.Dataframe, DatasetType.Json].includes(dataset.dataset_type) &&
      !dataset.has_original_blob)
  );
};

export const isCsv = (dataset: Dataset) => {
  return (
    dataset.has_original_blob &&
    (dataset.original_mime_type === 'text/csv' || dataset.original_mime_type === 'application/csv')
  );
};

export const isHtml = (dataset: Dataset) => {
  return dataset.has_original_blob && dataset.original_mime_type === 'text/html';
};

export const isMarkdown = (dataset: Dataset) => {
  // Internal diagnostic reports are output-backed files, so they do not have
  // an original blob even though their MIME type is still authoritative.
  return dataset.original_mime_type === 'text/markdown';
};

const TEXT_MIME_TYPES = ['text/plain', 'text/xml'];

export const isText = (dataset: Dataset) => {
  return (
    (dataset.has_original_blob && TEXT_MIME_TYPES.includes(dataset.original_mime_type || '')) ||
    (dataset.dataset_type === DatasetType.File && !dataset.has_original_blob)
  );
};

export const isMicrosoftOfficeFile = (dataset: Dataset) => {
  return (
    dataset.has_original_blob &&
    dataset.original_mime_type !== null &&
    [
      ...MS_OFFICE_EXCEL_MIME_TYPES,
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ].includes(dataset.original_mime_type)
  );
};

export function datasetTypeToString(datasetType: DatasetType): string {
  switch (datasetType) {
    case DatasetType.Directory:
      return 'Folder';
    case DatasetType.File:
      return 'File';
    case DatasetType.Dataframe:
      return 'List';
    case DatasetType.Json:
      return 'JSON';
    case DatasetType.Unknown:
      return 'Unknown';
    default:
      throw new Error('Unsupported dataset type');
  }
}
